import type { VariantProps } from 'class-variance-authority';

import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/utils';

const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                'default': 'bg-primary text-primary-foreground hover:bg-primary/90',
                'success': 'bg-success text-success-foreground hover:bg-success/90',
                'destructive': 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                'outline': 'border border-input bg-background-primary hover:bg-accent/70',
                'primary-outline': 'border border-primary text-primary font-semibold rounded-lg hover:bg-primary/10',
                'secondary': 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
                'ghost': 'hover:bg-accent hover:text-accent-foreground',
                'link': 'text-primary underline-offset-4 hover:underline',
                'input': `
                    flex h-10 w-full items-center justify-between gap-2 rounded-md
                    border border-input-border bg-transparent border-box'
                    px-3 py-2 text-sm
                    transition duration-100
                    placeholder:text-muted-foreground placeholder:font-normal hover:border-input-border-hover hover:shadow-input-hover hover:bg-transparent
                    focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
                    disabled:cursor-not-allowed disabled:opacity-50
                `,
            },
            size: {
                default: 'h-10 px-4 py-2',
                sm: 'h-9 rounded-md px-3',
                lg: 'h-11 rounded-md px-8',
                icon: 'h-10 w-10',
            },
            outline: {
                primary:
                    'border border-secondary text-primary hover:bg-secondary/10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

export type ButtonProps = {
    asChild?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants>;

function Button({ ref, className, variant, size, outline, asChild = false, type = 'button', ...props }: ButtonProps & { ref?: React.RefObject<HTMLButtonElement | null> }) {
    const Comp = asChild ? Slot : 'button';
    return (
        <Comp
            className={cn(
                buttonVariants({ variant, size, outline, className }),
            )}
            ref={ref}
            type={type}
            {...props}
        />
    );
}
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
